package co.maytes.standupbot.dashboard.features.home

import co.maytes.standupbot.shared.dto.integration.IntegrationProviderDto
import co.maytes.standupbot.shared.dto.setup.HealthEvidenceDto
import co.maytes.standupbot.shared.dto.setup.SetupItemDto
import co.maytes.standupbot.shared.dto.setup.SetupItemStateDto
import co.maytes.standupbot.shared.dto.setup.SetupStatusDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunStatusDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunSummaryDto
import co.maytes.standupbot.shared.util.RelativeTime
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone

enum class StripTone { Healthy, Degraded, Broken, Muted }

sealed interface StripAction {
    val label: String

    data class Reconnect(val provider: IntegrationProviderDto, override val label: String) : StripAction

    data class Open(override val label: String, val path: String) : StripAction
}

data class StripChip(
    val provider: IntegrationProviderDto,
    val name: String,
    val tone: StripTone,
    val stateWord: String,
    val evidence: String?,
    val detail: String?,
    val checked: String?,
    val action: StripAction?,
) {
    val unhealthy: Boolean get() = tone == StripTone.Degraded || tone == StripTone.Broken

    val ariaLabel: String get() = listOfNotNull(name, stateWord, evidence).joinToString(". ")
}

enum class StripMode { Hidden, Collapsed, Expanded }

data class HealthStripModel(
    val mode: StripMode,
    val chips: List<StripChip>,
    val summary: String,
    val canAct: Boolean,
) {
    val unhealthyChips: List<StripChip> get() = chips.filter { it.unhealthy }

    val memberLine: String?
        get() =
            unhealthyChips.takeIf { it.isNotEmpty() }?.let { broken ->
                broken.joinToString(" ") { chip -> "${chip.name} ${chip.memberPhrase()}." } + " Ask an admin."
            }

    companion object {
        private val ORDER =
            listOf(
                Entry("SLACK", IntegrationProviderDto.SLACK, "Slack"),
                Entry("LINEAR", IntegrationProviderDto.LINEAR, "Linear"),
                Entry("GITHUB", IntegrationProviderDto.GITHUB, "GitHub"),
                Entry("GOOGLE_CALENDAR", IntegrationProviderDto.GOOGLE_CALENDAR, "Google Calendar"),
                Entry("ANTHROPIC", IntegrationProviderDto.ANTHROPIC, "Anthropic"),
            )

        private const val GITHUB_INSTALLATIONS = "https://github.com/settings/installations"
        private const val SUSPENDED_PREFIX = "Suspended"

        fun hidden(): HealthStripModel = HealthStripModel(StripMode.Hidden, emptyList(), "", canAct = false)

        fun from(
            status: SetupStatusDto?,
            runs: List<StandupRunSummaryDto>,
            isAdmin: Boolean,
            now: Instant,
            tz: TimeZone,
        ): HealthStripModel {
            if (status == null || !coreConnected(status)) return hidden()
            val byKey = status.items.associateBy { it.key }
            val chips =
                ORDER.mapNotNull { entry -> byKey[entry.key]?.let { chip(entry, it, runs, isAdmin, now, tz) } }
                    .sortedBy { if (it.unhealthy) 0 else 1 }
            val anyUnhealthy = chips.any { it.unhealthy }
            val mode =
                when {
                    anyUnhealthy -> StripMode.Expanded
                    isAdmin -> StripMode.Collapsed
                    else -> StripMode.Hidden
                }
            return HealthStripModel(mode, chips, healthySummary(chips, runs, now, tz), canAct = isAdmin)
        }

        private fun coreConnected(status: SetupStatusDto): Boolean {
            val byKey = status.items.associateBy { it.key }
            return listOf("SLACK", "LINEAR").all { key ->
                val state = byKey[key]?.state
                state != null && state != SetupItemStateDto.TODO
            }
        }

        private fun chip(
            entry: Entry,
            item: SetupItemDto,
            runs: List<StandupRunSummaryDto>,
            isAdmin: Boolean,
            now: Instant,
            tz: TimeZone,
        ): StripChip {
            val tone = toneOf(item.state)
            val unhealthy = tone == StripTone.Degraded || tone == StripTone.Broken
            return StripChip(
                provider = entry.provider,
                name = entry.name,
                tone = tone,
                stateWord = stateWord(entry.provider, item, tone),
                evidence = evidence(entry.provider, item, tone, runs, now, tz),
                detail = item.detail?.takeIf { unhealthy },
                checked = item.evidence?.lastProbedAt?.let { "Checked ${RelativeTime.format(it, now, tz)}" },
                action = if (isAdmin && unhealthy) action(entry, item) else null,
            )
        }

        private fun toneOf(state: SetupItemStateDto): StripTone =
            when (state) {
                SetupItemStateDto.DONE -> StripTone.Healthy
                SetupItemStateDto.WARNING -> StripTone.Degraded
                SetupItemStateDto.BROKEN -> StripTone.Broken
                SetupItemStateDto.OPTIONAL, SetupItemStateDto.TODO -> StripTone.Muted
            }

        private fun stateWord(
            provider: IntegrationProviderDto,
            item: SetupItemDto,
            tone: StripTone,
        ): String =
            when {
                tone == StripTone.Healthy -> "Healthy"
                tone == StripTone.Muted -> "Not connected"
                tone == StripTone.Degraded && item.detail?.startsWith(SUSPENDED_PREFIX) == true -> "Suspended on GitHub"
                tone == StripTone.Degraded -> "Degraded"
                provider == IntegrationProviderDto.ANTHROPIC -> "Key needed"
                else -> "Reconnect required"
            }

        private fun evidence(
            provider: IntegrationProviderDto,
            item: SetupItemDto,
            tone: StripTone,
            runs: List<StandupRunSummaryDto>,
            now: Instant,
            tz: TimeZone,
        ): String? {
            if (tone == StripTone.Muted) return "Optional"
            val ev = item.evidence
            return when (provider) {
                IntegrationProviderDto.SLACK -> lastPost(runs, now, tz) ?: lastSuccess(ev, "Last call succeeded", now, tz)
                IntegrationProviderDto.LINEAR, IntegrationProviderDto.GITHUB ->
                    lastPull(ev, now, tz) ?: lastSuccess(ev, "Last call succeeded", now, tz)
                IntegrationProviderDto.ANTHROPIC ->
                    if (item.detail == SHARED_KEY) SHARED_KEY else lastSuccess(ev, "Last summary", now, tz)
                else -> lastSuccess(ev, "Last call succeeded", now, tz)
            }
        }

        private const val SHARED_KEY = "Using StandupBot's shared key"

        private fun lastPost(
            runs: List<StandupRunSummaryDto>,
            now: Instant,
            tz: TimeZone,
        ): String? =
            runs.filter { it.status == StandupRunStatusDto.POSTED && it.postedAt != null }
                .maxByOrNull { it.postedAt!! }
                ?.postedAt
                ?.let { "Last post ${RelativeTime.format(it, now, tz)}" }

        private fun lastPull(
            ev: HealthEvidenceDto?,
            now: Instant,
            tz: TimeZone,
        ): String? {
            val at = ev?.lastSyncAt ?: return null
            val people = ev.lastEventCount?.let { " for $it ${if (it == 1) "person" else "people"}," } ?: ""
            return "Last pulled$people ${RelativeTime.format(at, now, tz)}"
        }

        private fun lastSuccess(
            ev: HealthEvidenceDto?,
            prefix: String,
            now: Instant,
            tz: TimeZone,
        ): String? = ev?.lastSuccessAt?.let { "$prefix ${RelativeTime.format(it, now, tz)}" }

        private fun action(
            entry: Entry,
            item: SetupItemDto,
        ): StripAction? {
            val label = item.fix?.label
            return when {
                entry.provider == IntegrationProviderDto.GITHUB && item.detail?.startsWith(SUSPENDED_PREFIX) == true ->
                    StripAction.Open("Open GitHub", GITHUB_INSTALLATIONS)
                entry.provider == IntegrationProviderDto.ANTHROPIC ->
                    StripAction.Open(label ?: "Update key", item.fix?.path ?: "/app/settings/integrations?fix=anthropic")
                label != null -> StripAction.Reconnect(entry.provider, label)
                else -> null
            }
        }

        private fun healthySummary(
            chips: List<StripChip>,
            runs: List<StandupRunSummaryDto>,
            now: Instant,
            tz: TimeZone,
        ): String {
            if (chips.any { it.unhealthy }) return "${chips.count { it.unhealthy }} of ${chips.size} connections need attention."
            val parts =
                listOfNotNull(
                    "All connections healthy.",
                    lastPost(runs, now, tz)?.let { it.replace("Last post", "Last standup posted") + "." },
                    chips.firstOrNull { it.provider == IntegrationProviderDto.LINEAR }?.evidence?.let { "$it." },
                )
            return parts.joinToString(" ")
        }

        private fun StripChip.memberPhrase(): String =
            when (tone) {
                StripTone.Broken -> if (provider == IntegrationProviderDto.ANTHROPIC) "needs a working key" else "is disconnected"
                else -> "is having trouble"
            }

        private data class Entry(val key: String, val provider: IntegrationProviderDto, val name: String)
    }
}
